import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../../../utils/axios';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    signInWithCustomToken
} from 'firebase/auth';
import { sendRequest } from '../../../../utils/firebase';
import { auth } from '../../../../utils/firebase';
import { closeAuthDialog } from '../authDialog';
import { getFirebaseErrorMessage } from '../../../../view/authentication/firebase-error-codes/errorCodes';
import { errorToast } from '../../../../utils/toasts/toasts';


export const getUserDetail = createAsyncThunk(
    'auth/getUserDetail',
    async (uid, { rejectWithValue }) => {
        try {
            // Make a GET request to the backend API to fetch user details
            const response = await axios.post('/api/v2/users/user_search', {
                firebase_uid: uid
            });

            // Return the user data from the response
            return response.data.data;
        } catch (error) {
            // If an error occurs, return the error message
            errorToast(`${error.message} during user search`);
            return rejectWithValue(error.message);
        }
    }
);

export const updateUserById = createAsyncThunk(
    'auth/updateUserById',
    async ({ id, data }, { rejectWithValue, dispatch }) => {
        try {
            // Make a PUT request to the backend API to update user details
            // const response = await axios.post(`${localBackendUrl}/users/${id}`, data);
            const response = await axios.put(`/api/v2/users/${id}`, data);

            dispatch(setUser(response.data.data));

            return response.data.data;
        } catch (error) {
            // If an error occurs, return the error message
            return rejectWithValue(error.data);
        }
    }
);

/**
 * Initializes the auth session by listening to Firebase auth state changes.
 *
 * @function initializeSession
 * @returns {Function} Redux thunk that dispatches auth actions based on user state
 * @description
 * 1. Sets loading state to true initially
 * 2. Listens for auth state changes using Firebase's onAuthStateChanged
 * 3. Unsubscribes after first trigger to prevent multiple listeners
 * 4. If user exists, extracts basic user data and sets authenticated state
 * 5. If no user, sets appropriate error message
 * 6. Sets loading to false when complete
 */
export function initializeSession() {
    return (dispatch) => {
        // Step 1
        dispatch(setLoading(true));

        // Step 2
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            // Step 3
            unsubscribe(); // Unregister the listener after first trigger

            // Step 4
            if (user) {
                // Get the ID token
                // const idToken = await user.getIdToken();

                // // Store token in localStorage
                // localStorage.setItem('serviceToken', idToken);

                const userData = {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    emailVerified: user.emailVerified
                };

                // console.log("User data from Firebase:", userData);
                dispatch(setUser(userData));
                dispatch(setLoggedIn(true));
                // dispatch(setAuthInitialized(true));
            }
            // Step 5
            else {
                localStorage.removeItem('serviceToken');
                // errorToast('You are not signed in');
                // dispatch(setError('No user is signed in'));
                dispatch(setLoggedIn(false));
                // dispatch(setAuthInitialized(true));
            }

            // Step 6
            dispatch(setLoading(false));
            dispatch(setAuthInitialized(true));
        });
    };
}

/**
 * Redux async thunk action to register a new user.
 * @function registerUser
 * @async
 * @param {Object} payload - The user registration data
 * @param {Object} thunkAPI - The Redux Toolkit thunk API object
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected value
 * @param {Function} thunkAPI.dispatch - Function to dispatch additional actions
 * @returns {Promise<Object>} - Promise resolving to the registered user's data
 * @throws Will reject with error message if registration fails
 *
 * Process:
 * 1. Destructure the payload
 * 2. Retrieves the deviceToken from localStorage. If not found then sendRequest for it
 * 3. Make a post request to backend to register new user with payload
 * 4. Once reuqest fulfilled successfully then make signInWithEmailAndPassword using firebase
 * 5. Once the user is created then close the open dialog
 * 6. Returns the reponse data
 *
 * - Handle any error occurs
 */
export const registerUser = createAsyncThunk(
    'auth/registerUser',
    async (payload, { rejectWithValue, dispatch }) => {
        try {
            // Step 1
            const { email, name, dob, password, phone } = payload;

            // Step 2
            const deviceToken = localStorage.getItem('deviceToken') || (await sendRequest());

            // Step 3
            const response = await axios.post(`/api/v2/user/register`, {
                email,
                name,
                dob,
                device_token: deviceToken || undefined,
                password,
                phone
            });

            // Step 4
            const userCredentials = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredentials.user;

            // Step 5
            dispatch(closeAuthDialog());

            // Step 6
            return {
                id: response.data.data.id,
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                emailVerified: user.emailVerified,
                status: response.data.status
            };
        } catch (error) {
            // return if error occurs
            return rejectWithValue(error.response.data.errors);
        }
    }
);

/**
 * Asynchronous Redux Thunk action that sends an OTP to a specified email address.
 *
 * @function sentOtpToEmail
 * @async
 * @param {string} email - The email address where the OTP will be sent
 * @returns {Promise<Object>} - A promise that resolves to the API response data
 *
 * @description
 * Process:
 * 1. Make a POST request to the backend API endpoint and attach email in request body
 * 2. Returns the response data if successful
 *
 * @error Handles API errors by rejecting with the error message using rejectWithValue
 */
export const sentOtpToEmail = createAsyncThunk(
    'auth/sentOtpToEmail',
    async (email, { rejectWithValue }) => {
        try {
            // Step 1
            const response = await axios.post(`/api/v2/send-otp-mail`, {
                email
            });

            // Step 2
            return response.data;
        } catch (error) {
            // return if any error occurs
            return rejectWithValue(error.response.data.message);
        }
    }
);

/**
 * Redux thunk action creator that verifies a one-time password for user authentication.
 *
 * @async
 * @function verifyOtp
 * @param {Object} params - The parameters for OTP verification
 * @param {Object} thunkAPI - The Redux Toolkit thunk API object
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected value
 * @param {Function} thunkAPI.dispatch - Function to dispatch additional actions
 * @returns {Promise<Object>} - Promise resolving to the authenticated user's basic information
 * @throws Will reject with error message if verification fails
 * @description
 * Process:
 * 1. Sends email and OTP to backend verification endpoint and receive response from backend
 * 2. Signs in with Firebase using the custom token
 * 3. Closes the authentication dialog
 * 4. Returns the user's basic information
 *
 * Handles any errors by rejecting with the error message
 */
export const verifyOtp = createAsyncThunk(
    'auth/verifyOtp',
    async ({ email, otp }, { rejectWithValue, dispatch }) => {
        try {
            // Step 1
            const response = await axios.post(`/api/v2/verify-otp`, {
                email,
                otp
            });

            // Step 2
            const userCredentials = await signInWithCustomToken(auth, response.data.token);
            const user = userCredentials.user;

            // Step 3
            dispatch(closeAuthDialog());

            // Step 4
            return {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                emailVerified: user.emailVerified
            };
        } catch (error) {
            // return if any error occurs
            // console.log("Error in verifyOtp:", error);
            return rejectWithValue(error.response.data.message);
        }
    }
);

/**
 * Redux async thunk action to register a user with email verification.
 * @function registerWithVerify
 * @async
 * @param {Object} params - The parameters for registration
 * @param {string} params.email - The email address of the user
 * @param {string} params.otp - The one-time password for verification
 * @param {Object} thunkAPI - The Redux Toolkit thunk API object
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected value
 * @returns {Promise<Object>} - Promise resolving to the API response data
 *
 * Process:
 * 1. Make a api request to backend with params
 * 2. Return the successful response
 * Handle any error if occurs
 */
export const registerWithVerify = createAsyncThunk(
    'auth/registerWithVerify',
    async ({ email, otp }, { rejectWithValue }) => {
        try {
            // Step 1
            const response = await axios.post(`/api/v2/register/verify`, {
                email,
                otp
            });

            // Step 2
            return response.data;
        } catch (error) {
            // return if any error occurs
            return rejectWithValue(error.response.data.message);
        }
    }
);

/**
 * Async thunk action creator that resends OTP to the specified email.
 *
 * @function resendOtp
 * @async
 * @param {string} email - The email address to which the OTP will be resent
 * @returns {Promise<Object>} The response data from the API if successful
 * @throws {Error} Will reject with the error message if the API call fails
 *
 * Process:
 * 1. Makes a POST request to the resend-OTP endpoint with the provided email
 * 2. Returns the response data from the server if successful
 *
 * If an error occurs, rejects with the error message
 */
export const resendOtp = createAsyncThunk('auth/resendOtp', async (email, { rejectWithValue }) => {
    try {
        // Step 1
        const response = await axios.post(`/api/v2/resend-otp`, {
            email
        });

        // Step 2
        return response.data;
    } catch (error) {
        // return if any error occurs
        return rejectWithValue(error.message);
    }
});

/**
 * Redux async thunk to handle user login with email and password.
 *
 * @function loginWithEmailAndPassword
 * @async
 * @param {Object} credentials - The login credentials
 * @param {string} credentials.email - User's email address
 * @param {string} credentials.password - User's password
 * @returns {Object} User data including Firebase auth info and backend status
 *
 * Process:
 * 1. Authenticate with Firebase using email/password
 * 2. Get ID token from authenticated user
 * 3. Set Id Token in local storage
 * 4. Send credentials + ID token to backend API
 * 5. Set the idToken in localStorage
 * 6. Close the open auth dialog
 * 7. Return combined user data from Firebase and backend
 *
 * @throws Will return rejected promise with error message on failure
 */
export const loginWithEmailAndPassword = createAsyncThunk(
    'auth/loginWithEmailAndPassword',
    async ({ email, password }, { rejectWithValue, dispatch }) => {
        try {
            // Step 1
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Step 2
            const idToken = await user.getIdToken();

            // Step 3
            localStorage.setItem('serviceToken', idToken);

            // Step 4
            const response = await axios.post(`/api/v2/admin/login`, {
                email,
                password,
                id_token: idToken
            });

            // Step 5
            dispatch(closeAuthDialog());

            const userData = {
                id: response.data.data.id,
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                emailVerified: user.emailVerified,
                status: response.data.status
            };

            // Step 6
            return userData;
        } catch (error) {
            // return if any error occurs
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

/**
 * Async thunk action to sign out the current user from Firebase Auth.
 * @function signOutUser
 * @async
 * @returns {Promise<boolean>} Returns true if sign out was successful
 * @throws {Error} Rejects with error message if sign out fails
 * @description
 *
 * Process:
 * 1. Calls Firebase's `signOut` method to sign out the user.
 * 2. If successful, dispatches `setLoggedIn(false)` to update the state.
 * 3. Returns true to indicate successful sign out.
 */
export const signOutUser = createAsyncThunk(
    'auth/signOutUser',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            // Step 1
            await signOut(auth);

            // Clear the token from localStorage
            localStorage.removeItem('serviceToken');

            // Step 2
            dispatch(setLoggedIn(false));

            // Step 3
            return true;
        } catch (error) {
            // return errors if occurs
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Redux async thunk to check if an email exists in the system.
 *
 * @function checkEmailExists
 * @param {string} email - The email address to check
 * @returns {Promise<Object>} Response data if successful
 * @throws Will reject with error message if request fails
 * TODO: Remember to change the url once the backend is deployed with new URL
 *
 * Process:
 * 1. Sends a POST request to the backend API with the email.
 * 2. If the request is successful, returns the response data.
 */
export const checkEmailExists = createAsyncThunk(
    'auth/checkEmailExists',
    async (email, { rejectWithValue }) => {
        try {
            // Step 1
            const response = await axios.post(`/api/v2/admin/check-email`, {
                email
            });

            // Step 2
            return response.data;
        } catch (error) {
            // return if any error occurs
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Sends a password reset link to the provided email address.
 *
 * @async
 * @function sendPasswordResetLink
 * @param {string} email - The email address to send the password reset link to
 * @returns {Promise<Object>} The response data from the server
 * @throws {Error} Rejected with the error message if the request fails
 *
 * Process:
 * 1. Makes a POST request to the password-reset endpoint with the email
 * 2. Returns the response data if successful
 *
 * - Handle error if occurs
 */
export const sendPasswordResetLink = createAsyncThunk(
    'auth/sentPasswordResetLink',
    async (email, { rejectWithValue }) => {
        try {
            // Step 1
            const response = await axios.post(`/api/v2/password-reset`, { email });

            // Step 2
            return response.data;
        } catch (error) {
            // return if any error occurs
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Redux async thunk action that handles password reset functionality
 *
 * @async
 * @function resetPassword
 * @param {Object} payload - The data required for password reset => email and password
 * @returns {Promise<Object>} Promise resolving to the server response data
 * @throws {Error} Rejects with error message if the API request fails
 *
 * @process
 * 1. Creates an API POST request to the change-password endpoint and attache email and password
 * 2. Returns the response data if successful
 * Error handling:
 * - Catches any authentication errors
 * - Returns the formatted error message via rejectWithValue
 */
export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async ({ email, password }, { rejectWithValue }) => {
        try {
            // Step 1
            const response = await axios.post(`/api/v2/change-password`, {
                email,
                password
            });

            // Step 2
            return response.data;
        } catch (error) {
            // return if any error occurs
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Async thunk action creator for guest login functionality.
 *
 * @async
 * @function guestLogin
 * @param {Object} params - The login parameters
 * @param {Object} thunkAPI - The Redux Toolkit thunk API object
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected value
 * @param {Function} thunkAPI.dispatch - Redux dispatch function
 * @returns {Promise<Object>} User data object containing uid, email, phone, displayName, and emailVerified
 *
 * @description
 * Process:
 * 1. Make API request to register guest in backend database
 * 2. Closes the guest authentication dialog via dispatch
 * 3. Returns user information
 *
 * Error handling:
 * - Uses getFirebaseErrorMessage to format the error
 */
export const guestLogin = createAsyncThunk(
    'auth/guestLogin',
    async ({ email, phone }, { rejectWithValue, dispatch }) => {
        try {
            // Step 1
            await axios.post(`/api/v2/guest/register`, {
                email: email,
                phone: phone,
                device_token: localStorage.getItem('deviceToken') || (await sendRequest())
            });

            // Step 2
            dispatch(closeAuthDialog());

            // Step 3
            return {
                uid: null,
                email: email,
                phone: phone,
                displayName: 'Guest',
                emailVerified: false
            };
        } catch (error) {
            // return if any error occurs
            return rejectWithValue(getFirebaseErrorMessage(error));
        }
    }
);

// isAuthInitialized => the purpose of this state is to just check that authentication process is completes or not. no matter is user exists or not. Just to track the process. Mainly used for user protected routes
const initialState = {
    isLoggedIn: false,
    user: null,
    email: null,
    error: null,
    isAuthInitialized: false,
    loading: false
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setUser: (state, action) => {
            state.user = {
                ...state.user,     // keep existing user data
                ...action.payload  // override with new data
            };
        },
        setLoggedIn: (state, action) => {
            state.isLoggedIn = action.payload;
        },
        setEmail: (state, action) => {
            state.email = action.payload;
        },
        setAuthInitialized: (state, action) => {
            state.isAuthInitialized = action.payload;
        }
    },
    extraReducers: (builder) => {
        // getUserDetails
        builder
            .addCase(getUserDetail.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getUserDetail.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isLoggedIn = true;
                state.isAuthInitialized = true;
                // console.log("User details fetched successfully:", action.payload);
            })
            .addCase(getUserDetail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch user details';
                state.isLoggedIn = false;
                // state.isAuthInitialized = false;
                console.log('Error fetching user details:', action.payload);
            });

        // updateUserById
        builder
            .addCase(updateUserById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUserById.fulfilled, (state, action) => {
                state.loading = false;
                // state.user = action.payload;
                state.user = {
                    ...state.user,     // keep old fields
                    ...action.payload  // override with new fields
                };
                // console.log('User updated successfully:', action.payload);
            })
            .addCase(updateUserById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to update user';
                // console.log("Error updating user:", action.payload);
            });

        // checking if email exists
        builder
            .addCase(checkEmailExists.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(checkEmailExists.fulfilled, (state) => {
                state.loading = false;
                // console.log("Email exists check successful:", action.payload);
            })
            .addCase(checkEmailExists.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to check email existence';
            });

        // logging in user with email and password
        builder
            .addCase(loginWithEmailAndPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginWithEmailAndPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isLoggedIn = true;
                console.log('User logged in successfully:', action.payload);
            })
            .addCase(loginWithEmailAndPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to log in with email and password';
                state.isLoggedIn = false;
            });

        // signing out user
        builder
            .addCase(signOutUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(signOutUser.fulfilled, (state) => {
                state.loading = false;
                state.user = null;
                state.isLoggedIn = false;
                // console.log("User signed out successfully");
            })
            .addCase(signOutUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                // console.log("Error signing out:", action.payload);
            });

        // sending password reset link
        builder
            .addCase(sendPasswordResetLink.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(sendPasswordResetLink.fulfilled, (state, action) => {
                state.loading = false;
                // console.log("Password reset link sent successfully:", action.payload);
            })
            .addCase(sendPasswordResetLink.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to send password reset link';
            });

        // resetting password
        builder
            .addCase(resetPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.loading = false;
                console.log('Password changed successfully:', action.payload);
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to change password';
            });

        // guest login
        builder
            .addCase(guestLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(guestLogin.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isLoggedIn = true;
                // console.log("Guest user logged in successfully:", action.payload);
            })
            .addCase(guestLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to log in as guest';
                state.isLoggedIn = false;
            });

        // send otp
        builder
            .addCase(sentOtpToEmail.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(sentOtpToEmail.fulfilled, (state, action) => {
                state.loading = false;
                // console.log("OTP sent successfully:", action.payload);
            })
            .addCase(sentOtpToEmail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to send OTP';
            });

        // verify OTP
        builder
            .addCase(verifyOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(verifyOtp.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isLoggedIn = true;
                // console.log("OTP verified successfully:", action.payload);
            })
            .addCase(verifyOtp.rejected, (state, action) => {
                state.loading = false;
                state.isLoggedIn = false;
                state.error = action.payload || 'Failed to verify OTP';
                // console.log("Error verifying OTP:", action.payload);
            });

        // resend otp
        builder
            .addCase(resendOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(resendOtp.fulfilled, (state, action) => {
                state.loading = false;
                // console.log("OTP resent successfully:", action.payload);
            })
            .addCase(resendOtp.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to resend OTP';
            });

        // register with verify
        builder
            .addCase(registerWithVerify.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(registerWithVerify.fulfilled, (state, action) => {
                state.loading = false;
                // console.log("Registration verified successfully:", action.payload);
            })
            .addCase(registerWithVerify.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to verify registration';
            });

        // create user
        builder
            .addCase(registerUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isLoggedIn = true;
                state.email = null;
                // console.log("User created successfully:", action.payload);
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to create user';
                state.isLoggedIn = false;
            });
    }
});

export const {
    setLoading,
    setError,
    setUser,
    setLoggedIn,
    setEmail,
    setAuthInitialized
} =
    authSlice.actions;

export default authSlice.reducer;